import {isNil, isString} from 'lodash';
import rasterMapStylesFactory from 'Map/tier/styleParts/rasterMap';
import wmsMapStylesFactory from 'Map/tier/styleParts/wmsMap';
import styleResolver, {SOURCE_RASTER, SOURCE_VECTOR} from './styleParts/styleResolver';
import {vectorMapStyleResolver} from './styleParts/vectorStyleResolver';
import initializeStyleOption from 'Map/tier/styleParts/initializeStyleOption';
import {mapPolygonsUrlTransformer} from 'Map/tier/urlTransformers/urlTransformers';
import ThemeWrapper from '../configurer/styleOptionInitialization/themeWrapper';

export const TIER_TYPE = {
    BASE: 'BASE',
    LABELS: 'LABELS',
    BUILDINGS: 'BUILDINGS'
};

const FALLBACK_DATA_SOURCE = SOURCE_VECTOR;

class MapTier {
    constructor(options, position, tierType) {
        this.options = {...options};
        this.position = position;
        this.isSymbolLayer = tierType === TIER_TYPE.LABELS;
        this.tierType = tierType;
        const styleResolverFactory = (styleResolverOptions) => {
            const unwrapedTheme = styleResolverOptions.unwrapedTheme;
            return styleResolver(
                unwrapedTheme,
                () => rasterMapStylesFactory(
                    this.isSymbolLayer ? 'labels' : unwrapedTheme.layer,
                    unwrapedTheme.style,
                    this.isSymbolLayer ? 'labels' : ''
                ),
                () => vectorMapStyleResolver({...this.options, unwrapedTheme}),
                () => wmsMapStylesFactory(unwrapedTheme.format)
            );
        };

        this.style = initializeStyleOption(this.options, styleResolverFactory);
        const dataSource = (this.options.theme && this.options.theme.getSource()) || FALLBACK_DATA_SOURCE;
        this.urlTransformer = mapPolygonsUrlTransformer(this.options.key, dataSource,
            () => this.options.language, () => this.options.geopoliticalView);
    }
    getId = () => this.tierType;
    getPosition = () => this.position;
    getStyle = () => this.style;
    setStyle = (style) => {
        const newStyle = {...style};
        /**
         * This sprite and glyphs key replacement was added to keep consistency with other
         * {key} placeholders replacement. https://jira.tomtomgroup.com/browse/LNS-43133
         * Change was requested by Mateusz Szczepanczyk.
         */
        if (newStyle.sprite) {
            newStyle.sprite = newStyle.sprite.replace('{key}', this.options.key);
        }
        if (newStyle.glyphs) {
            newStyle.glyphs = newStyle.glyphs.replace('{key}', this.options.key);
        }
        switch (this.tierType) {
        case TIER_TYPE.BASE:
            newStyle.layers = [...newStyle.layers]
                .filter(layer => layer.type !== 'symbol' && layer.type !== 'fill-extrusion');
            break;
        case TIER_TYPE.LABELS:
            // we don't filter our (schema based) RASTER style because it doesn't have a symbols layer
            // but we DO WANT to filter all styles provided as objects or urls, because
            // we cannot make assumptions about their contents
            if (this.options.theme.getSource() === SOURCE_VECTOR ||
                this.options.theme.type !== ThemeWrapper.TYPE.RESOLVED_SCHEMA) {
                newStyle.layers = [...newStyle.layers]
                    .filter(layer => layer.type === 'symbol');
            }
            break;
        case TIER_TYPE.BUILDINGS:
            newStyle.layers = [...newStyle.layers]
                .filter(layer => layer.type === 'fill-extrusion');
            break;
        }

        this.style = newStyle;
    };
    getOptions = () => this.options;

    onAdd = (map) => {
        this._map = map;
    };

    _repaintTier = () => {
        if (isString(this.getStyle()) || !this._map) {
            return;
        }

        const sourceName = Object.keys(this.getStyle().sources)[0];
        this._map.__om.style.sourceCaches[sourceName].clearTiles();
        this._map.__om.getSource(sourceName).load();
    };

    setLanguage = (language) => {
        if (!isNil(language) && this.options.language !== language) {
            this.options.language = language;
            this._repaintTier();
            return true;
        }

        return false;
    };

    setGeopoliticalView = (geopoliticalView) => {
        if (!isNil(geopoliticalView) && this.options.geopoliticalView !== geopoliticalView) {
            this.options.geopoliticalView = geopoliticalView;
            this._repaintTier();
            return true;
        }

        return false;
    };

    getUrlTransformer = () => this.urlTransformer;

    isRaster = () => this.options && this.options.theme && this.options.theme.getSource() === SOURCE_RASTER;
}

export default (options, position, tierOptions) => {
    return new MapTier(options, position, tierOptions);
};



// WEBPACK FOOTER //
// ./map/tier/mapTier.js